// Строка пульта: человек с деньгами на руках.
//
// Крупно — неподтверждённый остаток, потому что именно по нему финансовый директор ранжирует
// утро. Второй строкой — кто он и сколько времени висит: сумма отвечает на вопрос «где
// больше всего», давность — на вопрос «где висит дольше всех», и это разные вопросы.
//
// ## Почему разложения остатка в строке больше нет
//
// До 25.08.2026 каждая строка несла четыре величины: выдано, подтверждено, на рассмотрении,
// не отчитано. Замысел был честный — показать глазом арифметику «остаток = на рассмотрении
// плюс не отчитано». На живых данных вышло иначе: у двадцати человек из двадцати «на
// рассмотрении» было ноль, «подтверждено» ноль, а «не отчитано» повторяло крупное число
// строкой ниже. То есть восемьдесят чисел на экране, из которых сорок — нули, а двадцать —
// повтор. Строка выросла до 113 точек, и на экран помещалось восемь человек вместо двадцати.
//
// Теперь разложение показывается тогда, когда оно ЧТО-ТО объясняет: часть остатка уже на
// рассмотрении, то есть человек отчитался, а финотдел ещё не принял. Когда объяснять нечего,
// строка молчит. Полное разложение со всеми четырьмя величинами никуда не делось — оно в
// карточке человека, куда ведёт эта же строка.

import { Link } from 'react-router';

import { ПРИЗНАК_СТРОКИ } from '@/layout/клавиатура-списка';
import type { PersonOut } from '@/shared/api/endpoints/accountable';
import { formatKopecks } from '@/shared/money/format';
import { pluralRu } from '@/shared/text/plural';
import { cn } from '@/utils/cn';

import { DAY_FORMS, LEVEL_TITLES } from '../dictionaries';

export interface PersonRowProps {
  person: PersonOut;
  href: string;
  selected: boolean;
}

export function PersonRow({ person, href, selected }: PersonRowProps) {
  const { balance } = person;
  // Разложение показывается, только когда остаток действительно разделён: иначе оно повторяет
  // крупное число и добавляет два нуля.
  const остатокРазделён = balance.under_review > 0 && balance.not_reported > 0;

  return (
    <Link
      to={href}
      aria-current={selected ? 'true' : undefined}
      // Признак строки — тот же, что у общей строки списка. Без него стрелки по пульту не
      // ходят вовсе: привычка «набрал фамилию, стрелка вниз, ввод» работает в реестре людей и
      // молча не работала там, где ВЫДАЮТ ДЕНЬГИ. Ход по списку живёт в колонке (`ListPane`),
      // и всё, что от строки нужно, — назваться строкой.
      {...{ [ПРИЗНАК_СТРОКИ]: '' }}
      className={cn(
        // Отступ 6 точек, а не 10, и просвет между строками 2, а не 4.
        //
        // Замер по кадру: строка выходила около 65 точек при норме 44-48 (`ПРАВИЛА.md`,
        // плотность), а при разделённом остатке до 101. Это ПЕРВЫЙ экран финансового
        // директора: «утро финдиректора» показывало десять-тринадцать человек из двадцати
        // трёх вместо семнадцати, то есть половину списка приходилось прокручивать.
        //
        // Общая строка списка эту норму держит с 4 сентября, а пульт рисует строки своими
        // средствами и правку тогда не получил — ровно тот случай, когда класс дефекта
        // объявлен закрытым по одному месту из трёх.
        // Поле 4, а не 6: строка выходила 55 точек при норме 52, которую держит сквозная
        // проверка. Разница в три строки на экран — это как раз те люди, до которых утром
        // приходилось докручивать. Отступы здесь считаны, а не подобраны: 4 + 24 (имя и сумма)
        // + 2 (просвет) + 16 (подпись) + 4 + 1 (черта) = 51.
        'flex flex-col gap-0.5 border-b border-stroke-soft-200 px-4 py-1 transition-colors',
        // Кольцо фокуса внутрь: наружное у строки во всю ширину колонки срезается её краем.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-base',
        selected ? 'bg-bg-weak-50' : 'hover:bg-bg-weak-50',
      )}
    >
      <div className='flex items-baseline justify-between gap-3'>
        <span className='truncate text-label-sm text-text-strong-950'>{person.employee_name}</span>
        <span
          className={cn(
            'shrink-0 text-label-lg tabular-nums',
            // Красный занят порогом. Отрицательный остаток — не ошибка, а «компания должна»:
            // красить его тем же цветом значит сказать неправду о том, кто кому должен.
            person.over_soft_limit ? 'text-error-base' : 'text-text-strong-950',
          )}
        >
          {formatKopecks(balance.unconfirmed_balance)}
        </span>
      </div>

      <div className='flex items-baseline justify-between gap-2 text-paragraph-xs text-text-sub-600'>
        <span className='truncate'>
          {LEVEL_TITLES[person.level] ?? person.level}
          {/* Ноль дней не пишем: «не отчитывается 0 дней» читается как претензия к человеку,
              который получил деньги час назад. Давность появляется со второго дня. */}
          {person.oldest_unreported_days !== null && person.oldest_unreported_days > 0 && (
            <>
              {' · не отчитывается '}
              {person.oldest_unreported_days} {pluralRu(person.oldest_unreported_days, DAY_FORMS)}
            </>
          )}
        </span>
        {person.over_soft_limit && person.soft_limit !== null && (
          // Цвет ДУБЛИРУЕТ слово, а не заменяет: на пересвеченном экране остаётся текст.
          //
          // Сама величина порога здесь не повторяется. Порог один на всех, и на живом экране
          // «выше порога 30 000,00» стояло в десяти строках из двенадцати — одно и то же
          // число десять раз подряд перестаёт читаться и превращается в узор. Точная величина
          // есть в карточке человека, куда ведёт строка.
          <span className='shrink-0 text-error-base'>выше порога</span>
        )}
        {person.soft_limit === null && balance.unconfirmed_balance > 0 && (
          // Третий факт, а не второй: «порог не превышен» и «порога нет вовсе» до 26.08.2026
          // выглядели одинаково — молчанием. На живом пульте это дало худший из возможных
          // видов: два самых крупных остатка (13,3 млн и 235 тысяч) стояли чёрными и молча,
          // а 76 тысяч рядом — красным «выше порога». Читается как «тринадцать миллионов
          // норма, а семьдесят шесть тысяч тревога», хотя правда в другом: у этих двоих порог
          // просто не задан. Пометка приглушённая — это не тревога, а недоделанная настройка,
          // и она называет, чего не хватает.
          <span className='shrink-0 text-text-sub-600'>порог не задан</span>
        )}
        {balance.company_owes && (
          <span className='shrink-0 text-text-sub-600'>компания должна</span>
        )}
      </div>

      {остатокРазделён && (
        <span className='truncate text-paragraph-xs text-text-sub-600'>
          на рассмотрении {formatKopecks(balance.under_review)} · не отчитано{' '}
          {formatKopecks(balance.not_reported)}
        </span>
      )}

      {balance.in_transit_out > 0 && (
        <span className='truncate text-paragraph-xs text-text-sub-600'>
          {formatKopecks(balance.in_transit_out)} в пути: передал, получатель ещё не подтвердил
        </span>
      )}
    </Link>
  );
}
